import { ActivatedRoute } from "@angular/router";
import { LabelData } from "navigation/list/navigation-entry.interface";

export type CommandType = "overwrite" | "restore";

type ProcessTreeItem = stages.process.ProcessTreeItem;
type ViewableElement = stages.process.ViewableElement;
type ViewableType = stages.process.ViewableType;

export abstract class ViewableNavigationStore {
	//The prefix for the type specific icon class. E.g. in case of the activity class "ico-et-activity" it is "et".
	abstract getTypeIconPrefix(): string;

	abstract getUppermostUpRoute(): unknown[];

	abstract getRoute(entry: ProcessTreeItem): unknown[];

	abstract restoreRouteFromParams(params: unknown): unknown[];

	//The subroute of the primary outlet after the workspace was changed in the clipboard. E.g. the route to index element of this element type
	abstract getRouteForClipboardWorkspaceChange(activeEntry: ViewableElement): unknown[];

	abstract getFilterForClipboardWorkspaceChange(
		activeEntry: ViewableElement,
	): { filterBy: string; filterParam1: string; filterParam2: string } | null;

	abstract isWorkspaceChangeSupportedForClipboard(action: string | null): boolean;

	//all navigation is done relative to this route
	abstract getBaseRoute(activatedRoute: ActivatedRoute): ActivatedRoute;

	//base route for popups, usually the base route of the module
	abstract getPopupBaseRoute(): unknown[];

	abstract getIdentifier(entry: ViewableElement): string;

	abstract getSelectionIdentifier(): string;

	abstract getLabel(entry: ViewableElement): LabelData;

	abstract rename(entry: ViewableElement, newLabel: string, workspaceId: string, pv: string): Promise<void>;

	abstract addChild(
		parent: ViewableElement,
		type: ViewableType,
		newLabel: string,
		workspaceId: string,
		pv: string,
		skipDuplicationCheck?: boolean,
	): Promise<ViewableElement>;

	abstract deleteSingle(entry: ViewableElement, workspaceId: string, pv: string): Promise<void>;

	abstract rearrange(
		parent: ViewableElement,
		sortedChildren: ViewableElement[],
		sortOrder: "alphabetical" | "custom",
		workspaceId: string,
		pv: string,
	): Promise<ViewableElement[]>;

	abstract move(
		targetEntry: ViewableElement,
		entries: ViewableElement[],
		workspaceId: string,
		pv: string,
	): Promise<void>;

	abstract copy(
		targetEntry: ViewableElement,
		entries: ViewableElement[],
		workspaceId: string,
		pv: string,
	): Promise<void>;

	abstract copyAssociations(
		sourceEntry: ViewableElement,
		targetEntry: ViewableElement,
		workspaceId: string,
		pv: string,
	): Promise<void>;

	abstract delete(entries: ViewableElement[], workspaceId: string, pv: string): Promise<void>;

	abstract executeCommand(
		commandType: CommandType,
		entry: ViewableElement,
		workspaceId: string,
		pv: string,
	): Promise<ViewableElement>;

	abstract executeBatchCommand(
		commandType: CommandType,
		targetEntry: ViewableElement,
		entries: ViewableElement[],
		workspaceId: string,
		pv: string,
	): Promise<void>;

	abstract getTypeMessageKeySingular(type: ViewableType): string;

	abstract getIconClassesForType(type: ViewableType): string[];

	//can be used to invalidate caches.
	abstract invalidate(): void;
}
